import { useState, useEffect, useCallback, useMemo } from 'react';
import { useClinicScope } from '@/shared/context/ClinicScopeContext';
import { clinicDisplayName } from './clinicDisplayName';

interface UseClinicScopeFieldOptions {
  open: boolean;
  initialClinicId?: string;
}

interface UseClinicScopeFieldResult {
  clinics: ReturnType<typeof useClinicScope>['clinics'];
  clinicId: string;
  setClinicId: (clinicId: string) => void;
  showClinicPicker: boolean;
  scopedClinicName: string | undefined;
  getClinicIdForSubmit: () => string | undefined;
  validateClinicSelection: () => string | null;
  resetClinicField: () => void;
}

const resolveClinicId = (
  initialClinicId: string | undefined,
  scopeSelected: string,
  clinics: ReturnType<typeof useClinicScope>['clinics']
): string => {
  if (initialClinicId) return initialClinicId;
  if (scopeSelected !== 'all') return scopeSelected;
  if (clinics.length === 1) return clinics[0]._id;
  return '';
};

export const useClinicScopeField = ({
  open,
  initialClinicId,
}: UseClinicScopeFieldOptions): UseClinicScopeFieldResult => {
  const { clinics, selected: scopeSelected } = useClinicScope();
  const [clinicId, setClinicId] = useState(() =>
    resolveClinicId(initialClinicId, scopeSelected, clinics)
  );

  const showClinicPicker = scopeSelected === 'all';

  const scopedClinicName = useMemo(() => {
    if (scopeSelected === 'all') return undefined;
    const clinic = clinics.find((clinic) => clinic._id === scopeSelected);
    return clinic ? clinicDisplayName(clinic) : undefined;
  }, [clinics, scopeSelected]);

  const resetClinicField = useCallback((): void => {
    setClinicId(resolveClinicId(initialClinicId, scopeSelected, clinics));
  }, [initialClinicId, scopeSelected, clinics]);

  useEffect(() => {
    if (!open) return;
    resetClinicField();
  }, [open, resetClinicField]);

  const getClinicIdForSubmit = useCallback((): string | undefined => {
    // AppBar scoped to one clinic → that id is the submit value (field is locked).
    if (scopeSelected !== 'all') {
      return scopeSelected;
    }

    const resolved = clinicId || (clinics.length === 1 ? clinics[0]._id : '');
    return resolved || undefined;
  }, [scopeSelected, clinicId, clinics]);

  const validateClinicSelection = useCallback((): string | null => {
    if (!showClinicPicker) {
      return null;
    }

    if (!getClinicIdForSubmit()) {
      return 'Vui lòng chọn chi nhánh';
    }

    return null;
  }, [showClinicPicker, getClinicIdForSubmit]);

  return {
    clinics,
    clinicId,
    setClinicId,
    showClinicPicker,
    scopedClinicName,
    getClinicIdForSubmit,
    validateClinicSelection,
    resetClinicField,
  };
};

/** @deprecated Use useClinicScopeField */
export const usePaymentClinicField = useClinicScopeField;
